import React, {useState, useCallback} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import {useApi} from '../../lib/api';
import {getErrorMessage} from '../../lib/helpers';
import Form from '../../components/Form';
import Input from '../../components/Input';
import TextArea from '../../components/TextArea';
import Button from '../../components/Button';
import Card from '../../components/Card';
import {T} from '../../components/I18n';
import {getUnitLabel} from '../FormBuilderScreen/shared';
import {formatQty, withUnit} from './shared';

export const MAX_REASON_LENGTH = 500;

const SET_COUNT = 'SET_COUNT';
const CHANGE_BY = 'CHANGE_BY';

//Mirrors the api's rules: a count is >= 0, a change is non-zero, a removal can't take the balance below zero, and a
//reason of at most 500 characters is required. Returns the amount as typed and parsed, alongside any errors.
export function validateAdjustment({mode, amount, reason}, currentBalance) {
  const errors = {};
  const trimmedAmount = `${amount}`.trim();
  const value = trimmedAmount === '' ? NaN : Number(trimmedAmount);
  let newBalance = null;

  if (!isFinite(value)) {
    errors.amount = mode === SET_COUNT ? 'Enter the counted amount' : 'Enter the amount to add or remove';
  }
  else if (mode === SET_COUNT) {
    if (value < 0) {
      errors.amount = 'A count can\'t be below zero';
    }
    else {
      newBalance = value;
    }
  }
  else if (value === 0) {
    errors.amount = 'Enter an amount other than zero';
  }
  else {
    newBalance = (currentBalance || 0) + value;

    //adding to a balance that is already short only moves it toward zero, so only a removal is held to this
    if (value < 0 && newBalance < 0) {
      errors.amount = 'This would take the balance below zero. Use "Set count to" to record a stock count instead.';
    }
  }

  const trimmedReason = reason.trim();

  if (!trimmedReason) {
    errors.reason = 'A reason is required';
  }
  else if (trimmedReason.length > MAX_REASON_LENGTH) {
    errors.reason = `The reason can be at most ${MAX_REASON_LENGTH} characters`;
  }

  return {errors, value, amount: trimmedAmount, newBalance, reason: trimmedReason};
}

function AdjustmentForm({item, onCancel, onSaved}) {
  const api = useApi();
  const [mode, setMode] = useState(SET_COUNT);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [validationEnabled, setValidationEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const validation = validateAdjustment({mode, amount, reason}, item.qty);
  const hasErrors = Object.keys(validation.errors).length > 0;
  const errors = validationEnabled ? validation.errors : {};

  const handleSubmit = useCallback(async () => {
    setValidationEnabled(true);

    if (hasErrors || saving) {
      return;
    }

    setSaving(true);

    try {
      //the amount goes as typed, since the api keeps up to 21 digits and a JS number would round it
      const adjustment = mode === SET_COUNT
        ? {newBalance: validation.amount, reason: validation.reason}
        : {delta: validation.amount, reason: validation.reason};

      await api.createInventoryAdjustment(item.itemId, adjustment);
      NotificationManager.success(<>The balance for <T noTranslate>{item.itemName}</T> was adjusted</>);
      setSaving(false);
      onSaved();
    }
    catch (e) {
      console.error(e);
      const fallback = 'Unable to adjust the balance. Please try again.';
      NotificationManager.error(e && e.status === 400 ? await getErrorMessage(e, fallback) : fallback);
      setSaving(false);
    }
  }, [api, item, mode, validation, hasErrors, saving, onSaved]);

  const unitLabel = getUnitLabel(item.unit);

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <Card.Content>
          <Form.Group>
            <Input
              type="select"
              id="inventory-adjustment-mode"
              label="Adjustment"
              value={mode}
              onChange={e => setMode(e.target.value)}>
              <option value={SET_COUNT}>Set count to</option>
              <option value={CHANGE_BY}>Add / remove</option>
            </Input>
          </Form.Group>
          <Form.Group>
            <Input
              type="number"
              step="any"
              min={mode === SET_COUNT ? 0 : undefined}
              id="inventory-adjustment-amount"
              label={`${mode === SET_COUNT ? 'Counted amount' : 'Amount to add (negative to remove)'}${unitLabel ? ` (${unitLabel})` : ''}`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              invalid={!!errors.amount}
              validationText={errors.amount}
              helpText={validation.newBalance !== null
                ? `New balance: ${withUnit(formatQty(validation.newBalance), item.unit)}`
                : `Current balance: ${withUnit(formatQty(item.qty), item.unit)}`}
            />
          </Form.Group>
          <Form.Group>
            <TextArea
              id="inventory-adjustment-reason"
              label="Reason *"
              placeholder="Why is the balance being changed?"
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              onChange={e => setReason(e.target.value)}
              invalid={!!errors.reason}
            />
            <Help invalid={!!errors.reason}>
              {errors.reason || `${reason.length}/${MAX_REASON_LENGTH} characters`}
            </Help>
          </Form.Group>
          <Actions>
            <Button type="submit" disabled={saving || (validationEnabled && hasErrors)}>
              {saving ? 'Saving...' : 'Save Adjustment'}
            </Button>
            <Button secondary onClick={onCancel} disabled={saving}>Cancel</Button>
          </Actions>
        </Card.Content>
      </Form>
    </Container>
  );
}

const Container = styled(Card)`
  margin-bottom: 1.5rem;
`;

const Help = styled.small`
  display: block;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  color: ${props => props.invalid ? '#dc3545' : '#747474'};
`;

const Actions = styled.div`
  margin-top: 1.5rem;

  > * + * {
    margin-left: 0.625rem;
  }
`;

export default AdjustmentForm;
